import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { Box, Typography, Paper } from '@mui/material';
import moment from 'moment';
import { LinkField } from 'framework/ui/field';
import { useVacancyGroupUrl } from 'domain/recruiting/newVacancy/routing';
import { useShowContext } from 'framework/show';
import ResubmitCandidateApplicationButton from 'domain/recruiting/candidate/applications/resubmit/ResubmitCandidateApplicationButton';
import { useDateService } from 'framework/utils';
import { Stages } from 'domain/shared/candidate';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import { Badge } from 'framework/ui/mui-builder';
import { BadgeLayoutProps } from 'framework/ui/uiContract';

interface ApplicationState {
  stage: string;
  rejectedFrom: string | null;
  jobsStates: any[];
  stageTransitionTimestamp: string;
  info?: any[];
  undoStates: string[];
}

interface Application {
  id: number;
  version: number;
  candidateId: string;
  candidateName: string;
  vacancyGroupAlias: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  applicationPipelineState: ApplicationState;
  source: string;
  sourceType: string;
  sourceComment: string | null;
  historySource: string | null;
  sourceEmployeeId: string | null;
  wasHired: boolean;
  isTestSubmission: boolean;
}

interface ApplicationsHistoryProps {
  applications: Application[];
}

const getStatusColor = (stage: string): BadgeLayoutProps['color'] => {
  switch (stage.toLowerCase()) {
    case 'rejected':
      return 'error';
    case 'hired':
      return 'success';
    case 'screening':
      return 'info';
    case 'applied':
      return 'warning';
    default:
      return 'default';
  }
};

const ApplicationsHistory: React.FC<ApplicationsHistoryProps> = ({ applications }) => {
  const { getSingleEntityUrl } = useVacancyGroupUrl();
  const { data } = useShowContext();
  const { getToday, getMomentDateObj, getDaysBetween } = useDateService();

  const isButtonVisible = (application: Application): boolean => {
    if (application.applicationPipelineState.stage !== Stages.Rejected) {
      return false;
    }

    const isAlreadyResubmitted = data.applications.result.some(
      (app: Application) =>
        app !== application &&
        app.vacancyGroupAlias === application.vacancyGroupAlias &&
        app.applicationPipelineState.stage !== Stages.Rejected,
    );

    return (
      !isAlreadyResubmitted && isDateWithinMonthPeriod(application.applicationPipelineState.stageTransitionTimestamp)
    );
  };

  const isDateWithinMonthPeriod = (stageTransitionTimestamp: string): boolean => {
    const dateToCheck = getMomentDateObj(stageTransitionTimestamp);
    const MONTH_DAYS = 30;

    if (dateToCheck) {
      const today = getToday();
      if (getDaysBetween(today, dateToCheck) <= MONTH_DAYS) {
        return true;
      }
    }
    return false;
  };

  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Application History
      </Typography>
      <Swiper
        modules={[Pagination]}
        pagination={{
          clickable: true,
          bulletActiveClass: 'swiper-pagination-bullet-active',
        }}
        spaceBetween={20}
        slidesPerView={1}
        style={{ padding: '0 0 40px 0' }}
        autoHeight={false}
      >
        {applications.map(application => {
          const vacancyGroup = data.vacancyGroups[application.vacancyGroupAlias];
          const vacancyGroupURL = vacancyGroup?.id ? getSingleEntityUrl(vacancyGroup.id) : '';

          return (
            <SwiperSlide key={application.id}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  margin: 1,
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '160px',
                  maxHeight: '200px',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <LinkField
                    isInternal
                    linkText={vacancyGroup?.specialization || application.vacancyGroupAlias}
                    value={vacancyGroupURL}
                  />
                  <Badge color={getStatusColor(application.applicationPipelineState.stage)}>
                    {application.applicationPipelineState.stage}
                  </Badge>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
                  {application.applicationPipelineState.rejectedFrom && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="subtitle1">Rejected From:</Typography>
                      <Typography>{application.applicationPipelineState.rejectedFrom}</Typography>
                    </Box>
                  )}
                  {application.applicationPipelineState.stage === 'Rejected' && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="subtitle1" flex="0 0 auto">
                        Rejected Reason:
                      </Typography>
                      <Typography>
                        {
                          application.applicationPipelineState.info?.find(info => info.type === 'Rejected')
                            ?.rejectedReason
                        }
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" gap={1}>
                    <Typography variant="subtitle1">Last Update:</Typography>
                    <Typography>{moment(application.updatedAt).format('MMM DD, YYYY')}</Typography>
                  </Box>
                </Box>

                {isButtonVisible(application) && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ResubmitCandidateApplicationButton applicationId={application.id} />
                  </Box>
                )}
              </Paper>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Box>
  );
};

export default ApplicationsHistory;
